import { useForm } from "react-hook-form";
import { ClassNames } from "@emotion/react";
import styled from "@emotion/styled";
import { DateTime, Duration } from "luxon";
import { Text } from "styles/utility-components";
import { z } from "zod";

import { Select } from "components/form_components/Inputs";
import { useFormAutoSubmit } from "lib/hooks/useFormAutoSubmit";
import { useURLState } from "lib/hooks/useURLState";
import { SupplierMentionType } from "lib/types/graphQLEnums";

// A mention is the supplier's name in a notice that doesn't list them as a supplier, often a
// subcontract. Mentioning notices of any stage count as notices, but only awards add to the value.
// Some of the most interesting awards are these, so they're in by default.
const MENTION_OPTIONS = [
  {
    label: "Include supplier mentions",
    description:
      "Awards naming this supplier, plus notices that mention them. Only awards add to the value, at their full value.",
    value: SupplierMentionType.MentionedOrDirectlyAwarded,
  },
  {
    label: "Exclude supplier mentions",
    description: "Only awards that name this supplier as a supplier.",
    value: SupplierMentionType.DirectlyAwarded,
  },
  {
    label: "Only supplier mentions",
    description:
      "Notices that mention this supplier without naming them as a supplier, such as subcontracts. Only awards add to the value, at their full value.",
    value: SupplierMentionType.MentionedInOnly,
  },
];

const INTERVAL_OPTIONS = [
  { label: "Monthly", value: "month" },
  { label: "Quarterly", value: "quarter" },
  { label: "Yearly", value: "year" },
] as const;

// Relative ranges in the same ISO-duration form the notice and spend date filters use, so a range
// means the same thing here as anywhere else a relative date is picked.
const RANGE_OPTIONS = [
  { label: "Last 12 months", value: "P-1Y" },
  { label: "Last 3 years", value: "P-3Y" },
  { label: "Last 5 years", value: "P-5Y" },
  { label: "All time", value: "all" },
] as const;

export type BuyerSupplierInterval = (typeof INTERVAL_OPTIONS)[number]["value"];
export type BuyerSupplierRange = (typeof RANGE_OPTIONS)[number]["value"];

export type BuyerSupplierView = {
  supplierMentionType: SupplierMentionType;
  interval: BuyerSupplierInterval;
  range: BuyerSupplierRange;
};

// Hoisted so each schema keeps one identity across renders.
const MENTION_SCHEMA = z.enum(SupplierMentionType);
const INTERVAL_SCHEMA = z.enum(["month", "quarter", "year"]);
const RANGE_SCHEMA = z.enum(["P-1Y", "P-3Y", "P-5Y", "all"]);

/**
 * The page's view, kept in the URL so a refresh or a shared link keeps it. Mentions included,
 * monthly over the last three years by default: long enough to show a pattern, short enough that
 * a recent relationship fills the chart.
 */
export function useBuyerSupplierView(): [
  BuyerSupplierView,
  (update: Partial<BuyerSupplierView>) => void,
] {
  const options = { replaceState: true };
  const [supplierMentionType, setMentionType] = useURLState(
    "mentionType",
    SupplierMentionType.MentionedOrDirectlyAwarded,
    MENTION_SCHEMA,
    options,
  );
  const [interval, setInterval] = useURLState("interval", "month", INTERVAL_SCHEMA, options);
  const [range, setRange] = useURLState("range", "P-3Y", RANGE_SCHEMA, options);

  const setView = (update: Partial<BuyerSupplierView>) => {
    if (update.supplierMentionType) setMentionType(update.supplierMentionType);
    if (update.interval) setInterval(update.interval);
    if (update.range) setRange(update.range);
  };

  return [{ supplierMentionType, interval, range }, setView];
}

/**
 * The first day the range covers, as YYYY-MM-DD, or undefined for all time. Worked out from today
 * on each call rather than once, so a page left open overnight still means what it says.
 */
export function rangeStart(range: BuyerSupplierRange): string | undefined {
  if (range === "all") {
    return undefined;
  }
  return DateTime.now().plus(Duration.fromISO(range)).toISODate() ?? undefined;
}

type Props = {
  view: BuyerSupplierView;
  /** Called with only the field that changed, so the rest of the view is untouched. */
  onChange: (update: Partial<BuyerSupplierView>) => void;
};

/**
 * The controls for the whole page: which awards count at the left, and when at the right. The
 * figures, the chart and both tables all follow them, so no section carries a filter of its own.
 */
export function BuyerSupplierViewControls({ view, onChange }: Props) {
  const { control, watch, getValues, trigger } = useForm<BuyerSupplierView>({
    defaultValues: view,
    values: view,
  });

  useFormAutoSubmit({
    watch,
    getValues,
    trigger,
    provideMetadata: true,
    onChange: (data, { name }) => {
      if (name === "supplierMentionType")
        onChange({ supplierMentionType: data.supplierMentionType });
      if (name === "interval") onChange({ interval: data.interval });
      if (name === "range") onChange({ range: data.range });
    },
  });

  return (
    <Controls aria-label="Page controls">
      <MentionControl>
        <ClassNames>
          {({ css }) => (
            <Select
              name="supplierMentionType"
              label=""
              aria-label="Supplier mentions"
              control={control}
              options={MENTION_OPTIONS}
              // The closed select shows the label; the open menu adds what each option includes.
              optionRender={(option) => (
                <OptionText>
                  <Text fontWeight="500">{option.data.label}</Text>
                  <Text description color="sysTextSecondary">
                    {option.data.description}
                  </Text>
                </OptionText>
              )}
              popupMatchSelectWidth={320}
              // Two-line options need room: a little more padding each, and a gap between them.
              popupClassName={css({
                "& .ant5-select-item": { paddingTop: 10, paddingBottom: 10 },
                "& .ant5-select-item + .ant5-select-item": { marginTop: 4 },
              })}
              // Three options never need virtualising, and a virtual list ignores the margins.
              virtual={false}
              data-testid="buyer-supplier-mention-type"
            />
          )}
        </ClassNames>
      </MentionControl>
      <Filters>
        <Control>
          <Select
            name="interval"
            label=""
            aria-label="Interval"
            control={control}
            options={[...INTERVAL_OPTIONS]}
            data-testid="buyer-supplier-interval"
          />
        </Control>
        <Control>
          <Select
            name="range"
            label=""
            aria-label="Date range"
            control={control}
            options={[...RANGE_OPTIONS]}
            data-testid="buyer-supplier-range"
          />
        </Control>
      </Filters>
    </Controls>
  );
}

// One row: mentions at the left, the time controls at the right. Only a narrow screen wraps it,
// and then the time controls move down together rather than one at a time.
const Controls = styled.div({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  flexWrap: "wrap",
  gap: 8,
});

const Filters = styled.div({
  display: "flex",
  flex: "0 0 auto",
  gap: 8,
});

const Control = styled.div({
  // A fixed width, as on the spend page, so the row doesn't reflow when a selection changes.
  width: 160,
  "& .ant5-select": {
    width: "100%",
  },
});

const MentionControl = styled(Control)({
  // Wide enough for its longest label, "Include supplier mentions".
  width: 240,
});

const OptionText = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: 2,
  whiteSpace: "normal",
});
